import { createContext, useContext, type ReactNode } from 'react'
import type { Locale, SiteCopy } from './types'
import { it } from './it'
import { en } from './en'

/**
 * Locale resolution for the Verde Vercelli 2 site.
 *
 * `COPY` holds one dictionary per registered locale (Italian and English).
 * The fallback to Italian is kept for a locale that has no dictionary yet,
 * so a half-translated build still renders rather than throws. The content
 * test iterates `REGISTERED_LOCALES`, so a dictionary is checked the moment
 * it is registered here.
 */
const COPY: Partial<Record<Locale, SiteCopy>> = { it, en }
export const REGISTERED_LOCALES: readonly Locale[] = Object.keys(COPY) as Locale[]

/** Falls back to Italian for a locale without a dictionary. */
export function getCopy(locale: Locale): SiteCopy {
  return COPY[locale] ?? it
}

const LocaleContext = createContext<Locale>('it')

export function LocaleProvider({ locale, children }: { locale: Locale; children: ReactNode }) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
}

export function useLocale(): Locale {
  return useContext(LocaleContext)
}

export function useCopy(): SiteCopy {
  return getCopy(useContext(LocaleContext))
}
